import {
  ChatBubbleLeftRightIcon,
  CircleStackIcon,
  ClipboardDocumentListIcon,
  QuestionMarkCircleIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline'
import { cn } from '@duck/ui'
import { Link } from '@tanstack/react-router'
import { useTar } from '@/modules'
import { trackMarketing } from '@/modules/marketingAnalytics'
import { GuideLastUpdated, IntentRelatedLinks } from './IntentRelatedLinks'

const TAKE_HOME_PRESSURE_CARDS = [
  {
    titleKey: 'takeHomePage.pressureCards.ambiguity.title',
    descriptionKey: 'takeHomePage.pressureCards.ambiguity.description',
    icon: QuestionMarkCircleIcon,
  },
  {
    titleKey: 'takeHomePage.pressureCards.messyData.title',
    descriptionKey: 'takeHomePage.pressureCards.messyData.description',
    icon: CircleStackIcon,
  },
  {
    titleKey: 'takeHomePage.pressureCards.tradeoffs.title',
    descriptionKey: 'takeHomePage.pressureCards.tradeoffs.description',
    icon: ScaleIcon,
  },
  {
    titleKey: 'takeHomePage.pressureCards.communication.title',
    descriptionKey: 'takeHomePage.pressureCards.communication.description',
    icon: ChatBubbleLeftRightIcon,
  },
] as const

const TAKE_HOME_PRACTICE_CARDS = [
  {
    titleKey: 'takeHomePage.practiceCards.scoping.title',
    descriptionKey: 'takeHomePage.practiceCards.scoping.description',
  },
  {
    titleKey: 'takeHomePage.practiceCards.validation.title',
    descriptionKey: 'takeHomePage.practiceCards.validation.description',
  },
  {
    titleKey: 'takeHomePage.practiceCards.reasoning.title',
    descriptionKey: 'takeHomePage.practiceCards.reasoning.description',
  },
  {
    titleKey: 'takeHomePage.practiceCards.iteration.title',
    descriptionKey: 'takeHomePage.practiceCards.iteration.description',
  },
] as const

const TAKE_HOME_LOOP_KEYS = [
  'takeHomePage.loopItems.repeatablePractice',
  'takeHomePage.loopItems.feedback',
  'takeHomePage.loopItems.patternRecognition',
] as const

export const HomeAssignmentsPage = () => {
  const { t } = useTar()

  return (
    <div className="flex flex-1 w-full justify-center">
      <div className="flex-1 min-w-0 max-w-6xl space-y-10 md:space-y-12 mt-4 md:mt-8 mb-16">
        <section
          className="grid grid-cols-1 md:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] items-start gap-10 px-6 py-8 md:px-10 md:py-12"
          aria-labelledby="take-home-heading"
        >
          <div className="flex flex-col items-start gap-6 max-w-xl">
            <h1
              id="take-home-heading"
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-foreground"
            >
              {t('takeHomePage.title')}
              <span className="text-accent">.</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              {t('takeHomePage.subtitle')}
            </p>
            <GuideLastUpdated isoDate="2026-05-09" />
            <p className="text-base md:text-lg text-foreground/90 leading-relaxed">
              {t('takeHomePage.introP1')}
            </p>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              {t('takeHomePage.introP2')}
            </p>
          </div>

          <aside className="rounded-xl border border-border bg-card/60 p-6 md:mt-8">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-accent">
              {t('takeHomePage.sidebarEyebrow')}
            </p>
            <p className="text-sm leading-7 text-foreground/90">
              {t('takeHomePage.sidebarBody')}
            </p>
          </aside>
        </section>

        <section className="space-y-8" aria-labelledby="pressure-heading">
          <div className="space-y-3">
            <h2
              id="pressure-heading"
              className="text-3xl font-bold text-foreground tracking-tight"
            >
              {t('takeHomePage.pressureTitle')}
            </h2>
            <p className="text-muted-foreground text-lg max-w-3xl leading-relaxed">
              {t('takeHomePage.pressureDescription')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
            {TAKE_HOME_PRESSURE_CARDS.map((card, index) => {
              const Icon = card.icon
              const isXlLeftEdge = index === 0
              const isMdRowStart = index % 2 === 0
              const isMdTopRow = index < 2
              return (
                <div
                  key={card.titleKey}
                  className={cn(
                    'px-6 py-5 space-y-3',
                    'md:py-2',
                    !isMdRowStart && 'md:border-s md:border-border/50',
                    !isMdTopRow && 'md:border-t md:border-border/50 md:pt-6',
                    'xl:border-s xl:border-border/50 xl:border-t-0 xl:pt-2',
                    isXlLeftEdge && 'xl:border-s-0'
                  )}
                >
                  <h3 className="flex items-baseline gap-2 text-lg font-semibold text-foreground">
                    <Icon aria-hidden className="h-4 w-4 self-center text-accent/70" />
                    {t(card.titleKey)}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    {t(card.descriptionKey)}
                  </p>
                </div>
              )
            })}
          </div>
        </section>

        <div aria-hidden className="h-px bg-border/50" />

        <section className="space-y-7" aria-labelledby="practice-heading">
          <div className="space-y-3">
            <h2
              id="practice-heading"
              className="text-3xl font-bold text-foreground tracking-tight"
            >
              {t('takeHomePage.practiceTitle')}
            </h2>
            <p className="text-muted-foreground text-lg max-w-3xl leading-relaxed">
              {t('takeHomePage.practiceDescription')}
            </p>
          </div>

          <ul className="grid grid-cols-1 md:grid-cols-2">
            {TAKE_HOME_PRACTICE_CARDS.map((card, index) => {
              const isLeftCol = index % 2 === 0
              return (
                <li
                  key={card.titleKey}
                  className={cn(
                    'px-1 py-6 md:px-7 md:py-7 space-y-2',
                    index < TAKE_HOME_PRACTICE_CARDS.length - 1 && 'border-b border-border/60',
                    index === TAKE_HOME_PRACTICE_CARDS.length - 2 && 'md:border-b-0',
                    isLeftCol && 'md:border-e md:border-border/60'
                  )}
                >
                  <h3 className="text-base md:text-lg font-semibold text-foreground leading-snug">
                    {t(card.titleKey)}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t(card.descriptionKey)}
                  </p>
                </li>
              )
            })}
          </ul>
        </section>

        <div aria-hidden className="h-px bg-border/50" />

        <section className="space-y-7" aria-labelledby="loop-heading">
          <div className="space-y-3">
            <h2
              id="loop-heading"
              className="text-3xl font-bold text-foreground tracking-tight"
            >
              {t('takeHomePage.loopTitle')}
            </h2>
            <p className="text-muted-foreground text-lg max-w-3xl leading-relaxed">
              {t('takeHomePage.loopDescription')}
            </p>
          </div>

          <ol className="grid grid-cols-1 md:grid-cols-3">
            {TAKE_HOME_LOOP_KEYS.map((key, index) => (
              <li
                key={key}
                className="px-6 py-5 md:py-3 md:border-s md:border-border/50 md:first:border-s-0 space-y-3"
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent/10 ring-1 ring-accent/20 text-sm font-semibold text-accent tabular-nums">
                    {index + 1}
                  </span>
                  <div aria-hidden className="h-px flex-1 bg-border/40" />
                </div>
                <p className="text-base leading-7 text-muted-foreground">{t(key)}</p>
              </li>
            ))}
          </ol>
        </section>

        <section
          className="relative isolate border-y border-border py-14 md:py-20"
          aria-labelledby="cta-heading"
        >
          <ClipboardDocumentListIcon
            aria-hidden
            className="pointer-events-none absolute -end-12 -top-10 h-72 w-72 text-accent/[0.13] -rotate-6 select-none"
          />
          <div className="relative space-y-6 max-w-2xl">
            <h2
              id="cta-heading"
              className="text-3xl md:text-4xl font-bold tracking-tight text-foreground"
            >
              {t('takeHomePage.ctaTitle')}
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed">
              {t('takeHomePage.ctaDescription')}
            </p>
            <Link
              to="/challenges/available"
              onClick={() => trackMarketing('guide:cta:click', { guide: 'home_assignments' })}
              className="group inline-flex items-center gap-2 rounded-lg bg-accent hover:bg-accent/85 text-accent-foreground text-base font-semibold px-5 py-3 shadow-[0_0_40px_-8px_hsl(var(--color-accent)/0.55)] transition-all hover:-translate-y-0.5"
            >
              {t('takeHomePage.ctaLabel')}
              <span
                aria-hidden
                className="ms-1 inline-block transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5"
              >
                →
              </span>
            </Link>
          </div>
        </section>

        <IntentRelatedLinks
          source="home_assignments"
          links={[
            { to: '/xp-lab-feedback', label: t('intentResources.feedbackTitle') },
            {
              to: '/faq#how-does-xp-lab-help-with-take-home-assignments',
              label: t('takeHomePage.faqCardTitle'),
            },
            {
              to: '/using-ai-without-outsourcing-judgment',
              label: t('intentResources.aiJudgmentTitle'),
            },
          ]}
        />
      </div>
    </div>
  )
}
